export function qptLayout(flow, components = []) {
  const allStages = (flow?.stages || []).slice().sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const isNoise = stage => /^(?:see|refer to)\s+(?:notes?|above|alternative)/i.test(`${stage.action || ""} ${stage.instruction || ""}`.trim());
  const stages = allStages.filter(stage => !isNoise(stage));
  const byId = new Map(stages.map(stage => [stage.id, stage]));
  const stageRefs = stage => (stage.input_refs || []).filter(ref => ref.startsWith("stage:")).map(ref => ref.slice(6));
  // A stage ref represents a prepared result, not another introduction of the
  // source ingredients. Preserve it as explicit renderer metadata.
  const outputLabel = stage => {
    const action = String(stage?.action || "").trim();
    return action && !/^step\s*\d+\s*[:.)-]?$/i.test(action)
      ? action
      : `Stage ${stage?.order || ""}`.trim();
  };
  const receivedStageOutputs = stage => [...new Set(stageRefs(stage))]
    .map(id => byId.get(id))
    .filter(Boolean)
    .map(source => ({ stage_id: source.id, label: outputLabel(source) }));
  const words = value => new Set(String(value || "").toLowerCase().replace(/cr[eè]me/g, "cream").match(/[a-z]{3,}/g) || []);
  const inferredRefs = stage => {
    // Legacy flows may have no component refs. Infer only an unambiguous full
    // component phrase; never let a generic word such as “water” place a later
    // addition at the first water stage.
    const text = words([stage.action, stage.instruction, stage.notes].filter(Boolean).join(" "));
    const matches = components.flatMap((component, index) => {
      const name = component.ingredient_name || component.prep_recipe_name || component.recipe_name || component.name || "";
      const tokens = [...words(name)].filter(token => !["amul", "fresh", "whole", "small", "large", "mixture", "ingredient"].includes(token));
      return tokens.length && tokens.every(token => text.has(token)) ? [`component:${index}`] : [];
    });
    return matches.length === 1 ? matches : [];
  };
  const componentRefs = stage => {
    const explicit = (stage.input_refs || []).filter(ref => ref.startsWith("component:"));
    return [...new Set(explicit.length ? explicit : inferredRefs(stage))].map(ref => Number(ref.split(":")[1])).sort((a, b) => a - b);
  };
  // A component belongs to the first stage where it enters the process. Later
  // references (for example "discard vanilla pod") are process context, not a
  // second ingredient entry. Component indexes are stable recipe-line refs.
  const firstEntryStage = new Map();
  stages.forEach(stage => componentRefs(stage).forEach(index => {
    if (!firstEntryStage.has(index)) firstEntryStage.set(index, stage.id);
  }));
  const entryRefs = stage => componentRefs(stage).filter(index => firstEntryStage.get(index) === stage.id);
  const rootIds = new Set(stages.filter(stage => entryRefs(stage).length).map(stage => stage.id));
  const initialRootIds = new Set(stages.filter(stage => entryRefs(stage).length && !stageRefs(stage).length).map(stage => stage.id));
  const sources = (stage, seen = new Set()) => {
    if (!stage || seen.has(stage.id)) return new Set();
    const next = new Set(seen); next.add(stage.id);
    const result = rootIds.has(stage.id) ? new Set([stage.id]) : new Set();
    stageRefs(stage).forEach(id => sources(byId.get(id), next).forEach(source => result.add(source)));
    return result;
  };
  const firstIngredientIndex = stages.findIndex(stage => componentRefs(stage).length > 0);
  const prePrep = (firstIngredientIndex < 0 ? stages : stages.slice(0, firstIngredientIndex)).filter(stage => !(stage.input_refs || []).length);
  const roots = stages.filter(stage => initialRootIds.has(stage.id));
  const joinsStreams = stage => /\b(temper|combine|fold|emulsi|incorporate)\b/i.test(`${stage.action || ""} ${stage.instruction || ""}`);
  const joinStages = stages.filter(stage => {
    if (rootIds.has(stage.id)) return false;
    const earlierRoots = roots.filter(root => stages.indexOf(root) < stages.indexOf(stage));
    return (stageRefs(stage).length > 1 && sources(stage).size > 1) || (joinsStreams(stage) && earlierRoots.length > 1);
  });
  const join = joinStages[0] || null;
  const joinIndex = join ? stages.indexOf(join) : -1;
  const beforeJoin = joinIndex >= 0 ? stages.slice(0, joinIndex) : roots;
  const streamRows = roots.map(root => ({ root, components: entryRefs(root), operations: beforeJoin.filter(stage => sources(stage).size === 1 && sources(stage).has(root.id) && !prePrep.includes(stage)) }));
  const represented = new Set([...prePrep, ...streamRows.flatMap(row => row.operations)]);
  joinStages.forEach(stage => represented.add(stage));
  const finalStages = stages.filter(stage => !represented.has(stage));

  const hasSavedDestination = stage => Boolean(
    (Array.isArray(stage?.equipment_refs) && stage.equipment_refs.length) || String(stage?.vessel_equipment || "").trim(),
  );
  const directContinuation = stage => stages.find(candidate =>
    stages.indexOf(candidate) > stages.indexOf(stage)
    && !componentRefs(candidate).length
    && stageRefs(candidate).length === 1
    && stageRefs(candidate)[0] === stage.id,
  );
  // Equipment can be saved on the first action *after* an ingredient-entry
  // node. Follow only that unambiguous single-stream link. This preserves the
  // saved graph rather than guessing from SOP prose, and never crosses a join
  // or another component entry.
  const destinationStageForEntry = entryStage => {
    let cursor = entryStage;
    const seen = new Set();
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      if (hasSavedDestination(cursor)) return cursor;
      cursor = directContinuation(cursor);
    }
    return entryStage;
  };
  const entryStageIndex = stage => stages.indexOf(stage);
  const rawEntryGroups = stages.filter(stage => entryRefs(stage).length).map(stage => ({
    stage,
    components: entryRefs(stage),
    destinationStage: destinationStageForEntry(stage),
  }));
  const entryIndexes = rawEntryGroups.map(group => entryStageIndex(group.stage));
  const lastEntryIndex = entryIndexes.length ? Math.max(...entryIndexes) : -1;
  const entryFlowStagesFor = entryStage => {
    const nextEntryIndex = entryIndexes.find(index => index > entryStageIndex(entryStage));
    const stagesInFlow = [entryStage];
    let cursor = entryStage;
    const seen = new Set([cursor.id]);
    // Keep a stream's own preparation beside its ingredients, but do not pull
    // post-addition work into the horizontal table. The final entry therefore
    // stops at its own stage; all later work becomes a full-width instruction.
    while (nextEntryIndex != null) {
      const continuation = directContinuation(cursor);
      if (!continuation || seen.has(continuation.id) || entryStageIndex(continuation) >= nextEntryIndex) break;
      stagesInFlow.push(continuation);
      seen.add(continuation.id);
      cursor = continuation;
    }
    return stagesInFlow;
  };
  const entryGroups = rawEntryGroups.map(group => ({
    ...group,
    processStages: entryFlowStagesFor(group.stage),
    receivedStageOutputs: receivedStageOutputs(group.stage),
  }));
  const entryProcessIds = new Set(entryGroups.flatMap(group => group.processStages.map(stage => stage.id)));
  // A join belongs in the compact grid only while ingredients are still being
  // introduced, or as the immediate first convergence after the final entry.
  // Subsequent sequential work is deliberately rendered below the table.
  const convergenceStages = joinStages.filter(stage => {
    const index = entryStageIndex(stage);
    return !entryProcessIds.has(stage.id) && index <= lastEntryIndex + 1;
  });
  const compactGridIds = new Set([...entryProcessIds, ...convergenceStages.map(stage => stage.id)]);
  const compactPostStages = stages.filter(stage => !prePrep.includes(stage) && !compactGridIds.has(stage.id));
  const hasLateEntries = entryGroups.some(group => stageRefs(group.stage).length > 0);
  // Retained as layout metadata for tests and consumers. The renderer uses the
  // entry rows and convergence stages below rather than one column per stage.
  const graphProcessStages = stages.filter(stage => !prePrep.includes(stage) && !compactPostStages.includes(stage));
  const legacyColumnCount = Math.min(4, graphProcessStages.length || 1);
  const legacyChunkSize = Math.ceil(graphProcessStages.length / legacyColumnCount);
  const processColumns = graphProcessStages.reduce((columns, stage, index) => {
    if (index % legacyChunkSize === 0) columns.push([]);
    columns[columns.length - 1].push(stage);
    return columns;
  }, []);

  // Merge a saved workstation only for consecutive dependent entries in the
  // same stream. Independent streams remain visibly separate even if they
  // happen to use bowls with the same display name.
  const destinationKey = stage => {
    const refs = Array.isArray(stage?.equipment_refs) ? stage.equipment_refs : [];
    return refs.length ? refs.map(ref => ref.equipment_id || ref.name).sort().join('|') : String(stage?.vessel_equipment || '').trim();
  };
  entryGroups.forEach(group => { group.vesselOwner = true; group.vesselRowSpan = 1; });
  entryGroups.forEach((group, index) => {
    if (!group.vesselOwner) return;
    const key = destinationKey(group.destinationStage);
    if (!key) return;
    for (let next = index + 1; next < entryGroups.length; next += 1) {
      const candidate = entryGroups[next];
      const continuesThisStream = stageRefs(candidate.stage).includes(entryGroups[next - 1].stage.id);
      if (!continuesThisStream || destinationKey(candidate.destinationStage) !== key) break;
      group.vesselRowSpan += 1;
      candidate.vesselOwner = false;
    }
  });
  return { prePrep, streamRows, join, joinStages, finalStages, componentRefs,
    graph: {
      entryGroups, processStages: graphProcessStages, processColumns, postStages: compactPostStages,
      convergenceStages, hasLateEntries,
      receivedStageOutputs: Object.fromEntries(stages.map(stage => [stage.id, receivedStageOutputs(stage)])),
    } };
}
